import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { baseurl } from '../utility/helper';
import { authCtx, ctxParams, rupee, toDateInput, CHAPTER_ADMIN } from '../utility/instituteHelpers';
import '../assets/css/Institute.css';

const emptyForm = {
  institute_id: '', batch_name: '', batch_code: '',
  start_date: '', end_date: '', requested_fee: '',
};

const BatchForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleid, chapterId } = authCtx();
  const { batchId, instituteId } = location.state || {};
  const isEdit = !!batchId;

  const [institutes, setInstitutes] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, institute_id: instituteId || '' });
  const [errors, setErrors] = useState({});
  const [loadedBatch, setLoadedBatch] = useState(null);
  const [editing, setEditing] = useState(false); // existing batches open read-only first
  const viewOnly = isEdit && !editing;

  useEffect(() => {
    axios.get(`${baseurl}/admin/institutes`)
      .then((res) => setInstitutes(res.data || []))
      .catch((err) => console.error('Error fetching institutes:', err));
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setLoadedBatch(null);
      setErrors({});
      setForm({ ...emptyForm, institute_id: instituteId || '' });
      return;
    }
    axios.get(`${baseurl}/admin/batches/${batchId}`)
      .then((res) => {
        const b = res.data;
        setLoadedBatch(b);
        setForm({
          institute_id: b.institute_id,
          batch_name: b.batch_name || '',
          batch_code: b.batch_code || '',
          start_date: toDateInput(b.start_date),
          end_date: toDateInput(b.end_date),
          requested_fee: b.requested_fee ?? '',
        });
      })
      .catch((err) => {
        console.error('Error loading batch:', err);
        Swal.fire('Error', 'Could not load batch', 'error');
      });
  }, [isEdit, batchId, instituteId]);

  const selectedInstitute = useMemo(
    () => institutes.find((i) => String(i.institute_id) === String(form.institute_id)),
    [institutes, form.institute_id]
  );

  // On edit, fee comparison is against the frozen snapshot; on create, against the live institute fee.
  const compareFee = isEdit && loadedBatch
    ? Number(loadedBatch.approved_fee_snapshot)
    : (selectedInstitute ? Number(selectedInstitute.approved_fee) : null);

  const feeHint = useMemo(() => {
    if (compareFee === null || form.requested_fee === '') return null;
    const diff = Number(form.requested_fee) - compareFee;
    if (diff === 0) {
      return { cls: 'match', text: `Matches the approved fee (${rupee(compareFee)}) — batch becomes Active immediately.` };
    }
    return {
      cls: 'differ',
      text: `Differs from approved fee (${rupee(compareFee)}) by ${rupee(Math.abs(diff))} ${diff > 0 ? 'higher' : 'lower'} — needs Super Admin approval.`,
    };
  }, [compareFee, form.requested_fee]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const err = {};
    if (!form.institute_id) err.institute_id = 'Select an institute';
    if (!form.batch_name.trim()) err.batch_name = 'Batch name is required';
    if (!form.batch_code.trim()) err.batch_code = 'Batch code is required';
    if (form.requested_fee === '' || Number(form.requested_fee) < 0) err.requested_fee = 'Enter a valid fee';
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      err.end_date = 'End date must be after start date';
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      batch_name: form.batch_name,
      batch_code: form.batch_code,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      requested_fee: Number(form.requested_fee),
      chapterId: roleid === CHAPTER_ADMIN ? chapterId : (selectedInstitute?.chapter_id || chapterId || null),
      ...ctxParams(),
    };

    try {
      let res;
      if (isEdit) {
        res = await axios.put(`${baseurl}/admin/batches/${batchId}`, payload);
      } else {
        res = await axios.post(`${baseurl}/admin/institutes/${form.institute_id}/batches`, payload);
      }
      const status = res.data?.status;
      await Swal.fire({
        title: isEdit ? 'Batch updated' : 'Batch created',
        text: status === 'Active'
          ? 'Fee matches the approved fee — the batch is Active.'
          : 'Fee differs from the approved fee — sent to Super Admin for approval.',
        icon: 'success',
      });
      navigate('/admin/BatchList');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save batch', 'error');
    }
  };

  const handleDelete = async () => {
    const c = await Swal.fire({
      title: 'Delete this batch?', text: "You won't be able to revert this!",
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete it!',
    });
    if (!c.isConfirmed) return;
    try {
      await axios.delete(`${baseurl}/admin/batches/${batchId}`, { params: ctxParams() });
      await Swal.fire('Deleted!', 'Batch removed.', 'success');
      navigate('/admin/BatchList');
    } catch (err) {
      const blocked = err.response?.status === 400;
      Swal.fire(blocked ? 'Cannot Delete' : 'Error', err.response?.data?.message || 'Failed to delete batch', blocked ? 'warning' : 'error');
    }
  };

  return (
    <div className="content container-fluid inst-wrap">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h3 className="mb-0"><b>{!isEdit ? 'Add Batch' : viewOnly ? 'Batch Details' : 'Edit Batch'}</b></h3>
        <div>
          {viewOnly && (
            <>
              <button type="button" className="btn btn-warning" onClick={() => setEditing(true)}>
                <i className="fas fa-edit" /> Edit
              </button>
            </>
          )}
          {isEdit && (
            <>
            &nbsp;&nbsp;
              <button type="button" className="btn btn-danger" onClick={handleDelete}>
                <i className="fas fa-trash" /> Delete Batch
              </button>
            </>
          )}
        </div>
      </div>

      <div className="inst-card">
        <form onSubmit={handleSubmit} className="row g-3">
          <fieldset disabled={viewOnly} className="row g-3 m-0 p-0 border-0">
          <div className="col-md-6">
            <label className="form-label">Institute</label>
            <select className="form-select" name="institute_id" value={form.institute_id}
              onChange={handleChange} disabled={isEdit || !!instituteId}>
              <option value="">-- Select Institute --</option>
              {institutes.map((i) => (
                <option key={i.institute_id} value={i.institute_id}>
                  {i.name} — {i.location} ({rupee(i.approved_fee)})
                </option>
              ))}
            </select>
            {errors.institute_id && <small className="text-danger">{errors.institute_id}</small>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Batch Name</label>
            <input className="form-control" name="batch_name" value={form.batch_name} onChange={handleChange} />
            {errors.batch_name && <small className="text-danger">{errors.batch_name}</small>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Batch Code</label>
            <input className="form-control" name="batch_code" value={form.batch_code} onChange={handleChange}
              placeholder="e.g. 2026-A" />
            {errors.batch_code && <small className="text-danger">{errors.batch_code}</small>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Requested Fee (₹)</label>
            <input type="number" min="0" step="0.01" className="form-control"
              name="requested_fee" value={form.requested_fee} onChange={handleChange} />
            {errors.requested_fee && <small className="text-danger">{errors.requested_fee}</small>}
            {feeHint && <div className={`inst-fee-hint ${feeHint.cls}`}>{feeHint.text}</div>}
          </div>

          <div className="col-md-6">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-control" name="start_date" value={form.start_date} onChange={handleChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">End Date</label>
            <input type="date" className="form-control" name="end_date" value={form.end_date} onChange={handleChange} />
            {errors.end_date && <small className="text-danger">{errors.end_date}</small>}
          </div>

          {isEdit && loadedBatch && (
            <div className="col-12">
              <small className="text-muted">
                Current status: <b>{loadedBatch.status}</b>.
                {!viewOnly && ' Changing the fee re-runs the approval check.'}
              </small>
            </div>
          )}
          </fieldset>

          <div className="col-12 text-center">
            <Link to="/admin/BatchList">
              <button type="button" className="btn btn-secondary">{viewOnly ? 'Back' : 'Cancel'}</button>
            </Link>
            {!viewOnly && (
              <>
                &nbsp;&nbsp;
                <button type="submit" className="btn btn-primary" style={{ borderRadius: 8 }}>
                  {isEdit ? 'Update Batch' : 'Create Batch'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchForm;
